<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import semverGte from 'semver/functions/gte';

import { UnraidLoading } from '@unraid/shared-components';
import UnraidSvgUiWarningFill from '@unraid/shared-components/assets/svg/ui/warning-fill.svg';
import UnraidSvgUtilMinus from '@unraid/shared-components/assets/svg/util/minus.svg';
import UnraidSvgUtilHelp from '@unraid/shared-components/assets/svg/util/help.svg';
import UnraidSvgUtilInfo from '@unraid/shared-components/assets/svg/util/info.svg';
import UnraidSvgUtilNewWindow from '@unraid/shared-components/assets/svg/util/new-window.svg';
import UnraidSvgUtilTerminal from '@unraid/shared-components/assets/svg/util/terminal.svg';

import { ERROR_CORS_403 } from '../../consts';
import { log } from '../../log';

export default {
  components: {
    UnraidLoading,
    UnraidSvgUiWarningFill,
    UnraidSvgUtilMinus,
    UnraidSvgUtilHelp,
    UnraidSvgUtilInfo,
    UnraidSvgUtilNewWindow,
    UnraidSvgUtilTerminal,
  },
  /** using a prop for hideMyServers rather than vuex.state so we can conditionally display this component for showing server state errors */
  props: ['errorStateMessageVisible', 'hideotherservers', 'hideMyServers'],
  data: () => ({
    offlineTimer: sessionStorage.getItem('offlineTimer') || false,
    restartTriggered: false,
  }),
  watch: {
    apikey(newVal, oldVal) { // watching for upc apikey usage
      if (!oldVal && newVal && !this.hideMyServers) { // sign in with plugin
        log.debug('[watch.apikey] connect to unraid-api', { newVal, oldVal });
        this.serversPlz();
      }
      if (oldVal && !newVal && !this.hideMyServers) { // sign out with plugin
        log.debug('[apikey removed] disconnect from unraid-api', { newVal, oldVal });
        this.stopGraphqlClient(oldVal);
      }
    },
    noRemoteApikeyRegisteredWithPlg(newVal, oldVal) { // watching for upc apikey usage
      if (!oldVal && newVal && !this.hideMyServers) { // sign in with plugin
        log.debug('[watch.noRemoteApikeyRegisteredWithPlg] connect to unraid-api', { newVal, oldVal });
        this.serversPlz();
      }
    },
    /**
     * watching for upc apikey usage
     * @note - we don't need stopGraphqlClient on sign out here b/c we should stay connected for server state updates.
     * */
    registered(newVal, oldVal) {
      if (!oldVal && newVal && !this.hideMyServers) { // sign in
        log.debug('[watch.apikey] connect to unraid-api', { newVal, oldVal });
        this.serversPlz();
      }
    },
    /**
     * There was previously an error, but now there's not. If there isn't server data attempt to fetch servers
     */
    myServersError(newVal, oldVal) {
      if (oldVal && !newVal && !this.myServers.length) {
        log.debug('[watch.myServersError] connect to unraid-api', { newVal, oldVal });
        this.serversPlz();
      }
    },
    graphOnline(newVal, oldVal) {
      // when graph goes offline start a timer for displaying restart api btn
      if (oldVal && newVal === false) this.timer('watch.graphOnline');
    },
  },
  computed: {
    ...mapGetters([
      'errorTooManyDisks',
      'hasRemoteApikey',
      'keyTypeForPurchase',
      'myServersOutOfDate',
      'noRemoteApikeyRegisteredWithPlg',
      'plgInstallFailed',
      'stateData',
      'validApiKeyLength',
    ]),
    ...mapState([
      'apikey',
      'apiVersion',
      'cloud',
      'deprecatedUnraidSSL',
      'deviceCount',
      'graphOnline',
      'guid',
      'locale',
      'myServers',
      'myServersError',
      'myServersErrorShowSupportBtn',
      'myServersLoading',
      'pirateKey',
      'registered',
      'servername',
      'accountRegisterPayload',
      'state',
      'wanip',
    ]),
    currentServer() {
      if (!this.myServers) {
        return {
          name: this.servername,
          status: 'offline',
        };
      }
      return this.myServers.filter(x => ((x.guid === this.guid) ? x : null))[0];
    },
    currentServerStatus() {
      return this.currentServer ? this.currentServer.status : 'offline';
    },
    filteredServers() {
      if (!this.myServers) return [];
      return (this.hideotherservers) ? [] : this.myServers
        .filter(x => ((x.guid !== this.guid) ? x : null)) // remove current server
        .sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true, sensitivity: 'base' })); // sort them homies alphabetically
    },
    errorToggleText() {
      if (this.stateData.error.message) return this.state === 'EEXPIRED' ? this.stateData.error.heading : this.$t('fixError');
      if (this.pirateKey
        || this.myServersError
        || this.noRemoteApikeyRegisteredWithPlg
        || this.errorTooManyDisks
        || (this.myServersOutOfDate && this.myServersOutOfDate.enabled)
        || (this.cloud && this.cloud.error)
        || this.plgInstallFailed
        || this.deprecatedUnraidSSL) return this.computedErrorHeading;
      return '';
    },
    errorToggleTitle() {
      if (this.stateData.error
        || this.stateData.message
        || this.myServersError
        || (this.myServersOutOfDate && this.myServersOutOfDate.enabled)
        || (this.cloud && this.cloud.error)
        || this.plgInstallFailed
        || this.deprecatedUnraidSSL) {
        const start = ((this.stateData.error.message || this.stateData.message) && !this.noRemoteApikeyRegisteredWithPlg && !this.errorTooManyDisks && (this.cloud && !this.cloud.error))
          ? this.stateData.humanReadable : this.computedErrorHeading;
        const end = this.errorStateMessageVisible ? this.$t('upc.myServers.closeDetails') : this.$t('learnMore');
        return `${start} • ${end}`;
      }
      return '';
    },
    computedErrorHeading() {
      if (this.noRemoteApikeyRegisteredWithPlg) return this.$t('upc.noRemoteApikeyRegisteredWithPlg.heading');
      if (this.stateData.error.heading) return this.stateData.error.heading;
      if (this.pirateKey) return this.$t('keyFileNotValid');
      if (this.myServersLoading && this.restartTriggered) return 'Unraid API';
      if (this.myServersError) {
        return this.myServersError.includes('Network error')
          ? this.$t('upc.myServers.errors.unraidApi.heading')
          : this.$t('upc.myServers.errors.myServers.heading');
      }
      if (this.errorTooManyDisks) return this.$t('upc.errorTooManyDisks.heading');
      if (this.plgInstallFailed) return this.$t('installFailed.heading');
      if (this.deprecatedUnraidSSL) return 'unraid.net SSL certificates have been deprecated';
      if (this.myServersOutOfDate.enabled) return this.myServersOutOfDate.heading;
      if (this.cloud && this.cloud.error) return this.$t('upc.myServers.errors.unraidApi.heading');
      return '';
    },
    computedError() {
      if (this.noRemoteApikeyRegisteredWithPlg) return this.$t('upc.noRemoteApikeyRegisteredWithPlg.msg');
      if (this.stateData.error.message) return this.stateData.error.message;
      if (this.pirateKey) return this.$t('yargYePirate');
      if (this.myServersLoading && this.restartTriggered) return this.$t('upc.myServers.restarting');
      if (this.myServersError) {
        return this.myServersError.includes('Network error')
          ? this.$t('upc.myServers.errors.unraidApi.message')
          : this.myServersError;
      }
      if (this.errorTooManyDisks) {
        const msg = this.$t('upc.errorTooManyDisks.msg.base');
        if (this.keyTypeForPurchase === 'Basic') return `${this.$t('upc.errorTooManyDisks.msg.basic')} ${msg}`;
        if (this.keyTypeForPurchase === 'Plus') return `${this.$t('upc.errorTooManyDisks.msg.plus')} ${msg}`;
        return msg;
      }
      if (this.plgInstallFailed) return 'The Connect plugin installation failed. Rebooting will likely solve this.'; // this.$t('installFailed.message')
      if (this.deprecatedUnraidSSL) return 'You need to provision a new SSL certificate on the myunraid.net domain ASAP before you lose access to the webgui.';
      if (this.myServersOutOfDate.enabled) return this.myServersOutOfDate.message;
      if (this.cloud && this.cloud.error) return this.cloud.error;
      return '';
    },
    apiEnableRestartButton() {
      return (this.apiVersion ? semverGte(this.apiVersion, '2.42.5') : false)
        && this.offlineTimer
        && !this.hideMyServers
        && !this.graphOnline
        && !this.myServersLoading
        && !this.stateData.error
        && this.myServersError
        && (this.myServersError.includes('unraid-api is stopped') || this.myServersError.includes('Disconnected from Unraid API'));
    },
    /**
     * API ERROR MESSAGE 'OK' or 'SERVER_OVERLOAD' show specific thread
     * Otherwise link to basic plugin support forum
     */
    forumSupportLink() {
      if (this.plgInstallFailed) return 'https://forums.unraid.net/topic/112073-my-servers-releases/#comment-1154449';
      if (this.myServersError && this.myServersError.includes(ERROR_CORS_403)) return '/Settings/ManagementAccess#MyServersSettings';
      if (
        ((this.myServersError && this.myServersError.includes('OK'))
        || (this.myServersError && this.myServersError.includes('SERVER_OVERLOAD')))
        || (this.cloud && this.cloud.error && (this.cloud.error.includes('OK') || this.cloud.error.includes('SERVER_OVERLOAD')))
      ) {
        return 'https://forums.unraid.net/topic/112073-my-servers-releases/#comment-1121459';
      }
      if (this.deprecatedUnraidSSL) return 'https://unraid.net/blog/ssl-certificate-update';
      return 'https://forums.unraid.net/forum/94-my-servers-plugin-support/';
    },
    showForumSupportLink() {
      return (this.deprecatedUnraidSSL || this.plgInstallFailed || this.myServersError || (this.cloud && this.cloud.error)) && !this.stateData.error && this.errorStateMessageVisible;
    },
    forumSupportLinkText() {
      if (this.myServersError && this.myServersError.includes(ERROR_CORS_403)) return 'Connect Settings';
      return ((this.plgInstallFailed || this.deprecatedUnraidSSL) ? this.$t('learnMore') : 'Connect Support');
    },
    showOutOfDateMessage() {
      return !this.showForumSupportLink
        && (this.computedError
          && !this.stateData.error
          && this.errorStateMessageVisible
          && this.myServersOutOfDate
          && this.myServersOutOfDate.enabled
          && this.myServersOutOfDate.link);
    },
  },
  methods: {
    ...mapActions([
      'executeUnraidApi',
      'fetchServers',
      'openPopUp',
      'stopGraphqlClient',
      'installPlugin',
    ]),
    serversPlz() {
      log.debug('[serversPlz]', {
        apikey: this.apikey,
        noRemoteApikeyRegisteredWithPlg: this.noRemoteApikeyRegisteredWithPlg,
        validApiKeyLength: this.validApiKeyLength,
        myServersOutOfDate: this.myServersOutOfDate,
      });
      if (this.plgInstallFailed) return log.error('[MyServers] plugin failed to install correctly');
      if (this.myServersOutOfDate && this.myServersOutOfDate.enabled) {
        if (!this.myServersOutOfDate.error) log.warn(this.myServersOutOfDate.message); // only warn if not recommended api version
        if (this.myServersOutOfDate.error) return log.error(this.myServersOutOfDate.message); // only prevent if not minimum api version
      }
      if (this.noRemoteApikeyRegisteredWithPlg) return log.error('[MyServers] remote apikey does not exist');
      if (this.validApiKeyLength) {
        this.fetchServers();
        return this.timer('serversPlz()');
      }
      return log.error('[MyServers] invalid apikey');
    },
    /**
     * @name timer
     * @description if after 30secs the api isn't started we want to possibly enable apiEnableRestartButton
     * @param {String} from
     */
    timer(from = '') {
      log.debug('[timer] %s', from);
      setTimeout(() => {
        if (!this.graphOnline && this.registered) {
          this.offlineTimer = true;
          sessionStorage.setItem('offlineTimer', Date.now());
        }
      }, 30000);
    },
    triggerRestart() {
      this.executeUnraidApi({ command: 'start' });
      // reset so the timer can be used again without a page refresh
      this.offlineTimer = false;
      this.restartTriggered = true;
      sessionStorage.removeItem('offlineTimer');
    },
  },
  created() {
    if (!this.hideMyServers && this.registered) this.serversPlz();
  },
};
</script>

<template>
  <div class="relative">
    <ul class="relative flex flex-col gap-y-4px">
      <li v-if="computedError" key="current-server-error" class="w-full">
        <button
          @click="$emit('toggle-error')"
          class="btn-error group py-8px px-16px flex flex-col"
          :title="errorToggleTitle">
          <div class="w-full flex flex-row justify-between items-center">
            <span class="inline-flex flex-row items-center">
              <!-- show info icon for non-error of myServersOutOfDate b/c it still allows the API to connect -->
              <UnraidSvgUtilInfo v-if="myServersOutOfDate && myServersOutOfDate.enabled && !myServersOutOfDate.error" class="fill-current w-12px h-12px" />
              <UnraidSvgUiWarningFill v-else class="fill-current w-12px h-12px" />
              <span class="font-semibold mx-8px">
                {{ servername }}
              </span>
            </span>
            <UnraidSvgUtilMinus v-if="computedError && errorStateMessageVisible" class="text-current opacity-100 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
            <UnraidSvgUtilInfo v-else class="text-current opacity-100 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
          </div>
          <!-- ERROR MESSAGE -->
          <transition name="fade" mode="out-in">
            <div v-if="computedError && errorStateMessageVisible" class="UnraidUPC-serverError">
              <div class="text-14px font-semibold pt-4px">
                <h3 class="text-16px mb-4px">{{ errorToggleText }}</h3>
                <p v-html="computedError"></p>
              </div>
            </div>
          </transition>
        </button>
      </li>
      <!-- link to pirate landing page -->
      <transition name="fade" mode="out-in">
        <li v-if="pirateKey && errorStateMessageVisible">
          <a
            :href="locale === 'zh_CN' ? 'https://unraid.net/zh/%E8%B4%AD%E4%B9%B0-%E6%AD%A3%E7%89%88-unraid-%E8%AE%B8%E5%8F%AF%E8%AF%81' : 'https://unraid.net/buy-genuine-license'"
            :title="$t('upc.opensNewHttpsWindow.base', [$t('learnMore')])"
            target="_blank"
            class="btn-emphasize group flex flex-row items-center justify-between"
          >
            <span class="inline-flex flex-row items-center">
              <UnraidSvgUtilHelp class="fill-current w-12px h-12px mr-8px" />
              {{ $t('learnMore') }}
            </span>
            <UnraidSvgUtilNewWindow class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
          </a>
        </li>
      </transition>
      <!-- display outage error message link -->
      <transition name="fade" mode="out-in">
        <li v-if="showOutOfDateMessage">
          <button
            @click="installPlugin({ plgType: '', update: true })"
            class="btn-emphasize group flex flex-row items-center justify-between"
            :title="'Update Plugin'"
          >
            <span class="inline-flex flex-row items-center">
              <UnraidSvgUtilTerminal class="fill-current w-12px h-12px mr-8px" />
              {{ 'Update Plugin' }}
            </span>
          </button>
        </li>
      </transition>
      <!-- plugin support forum -->
      <transition name="fade" mode="out-in">
        <li v-if="showForumSupportLink">
          <a
            :href="forumSupportLink"
            :title="forumSupportLink.includes('https://') ? $t('upc.opensNewHttpsWindow.base', [forumSupportLinkText]) : ''"
            :target="forumSupportLink.includes('https://') ? '_blank' : ''"
            rel="noopener"
            class="btn-emphasize group flex flex-row items-center justify-between"
          >
            <span class="inline-flex flex-row items-center">
              <UnraidSvgUtilHelp class="fill-current w-12px h-12px mr-8px" />
              {{ forumSupportLinkText }}
            </span>
            <UnraidSvgUtilNewWindow v-if="forumSupportLink.includes('https://')" class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
          </a>
        </li>
      </transition>
      <!-- display contact support btn only when stateData.error not API related error unless myServersErrorShowSupportBtn is true -->
      <!-- temp removal `&& myServersErrorShowSupportBtn` -->
      <transition name="fade" mode="out-in">
        <li v-if="(((state !== 'EEXPIRED' && stateData.error) || pirateKey) && errorStateMessageVisible)">
          <a @click="$emit('contact-support')" :title="$t('upc.opensNewHttpsWindow.base', [$t('contactSupport')])" class="link-error group flex flex-row items-center justify-between">
            <span class="inline-flex flex-row items-center">
              <UnraidSvgUtilHelp class="fill-current w-12px h-12px mr-8px" />
              {{ $t('contactSupport') }}
            </span>
            <UnraidSvgUtilNewWindow class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
          </a>
        </li>
      </transition>
      <!-- when no remote apikey we need to have the user re-authenticate with Unraid.net to receive the apikey from the sign in response -->
      <transition name="fade" mode="out-in">
        <li v-if="noRemoteApikeyRegisteredWithPlg && errorStateMessageVisible">
          <a
            @click="openPopUp(accountRegisterPayload)"
            :title="$t('upc.opensNewHttpsWindow.base', [$t('signIn')])"
            class="btn-emphasize link-error group flex flex-row items-center justify-between"
          >
            <span class="inline-flex flex-row items-center">
              <UnraidSvgUiWarningFill class="fill-current w-12px h-12px mr-8px" />
              {{ $t('upc.signInActions.resolve') }}
            </span>
            <UnraidSvgUtilNewWindow class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out fill-current w-12px h-12px ml-8px" />
          </a>
        </li>
      </transition>
      <!-- display restart api button if after 30seconds of the page loading we're not connected to the api -->
      <transition name="fade" mode="out-in">
        <li v-if="apiEnableRestartButton">
          <button
            @click="triggerRestart()"
            class="btn-emphasize group flex flex-row items-center justify-between"
            :title="$t('upc.myServers.api.startTitle')"
          >
            <span class="inline-flex flex-row items-center">
              <UnraidSvgUtilTerminal class="fill-current w-12px h-12px mr-8px" />
              {{ $t('upc.myServers.api.start') }}
            </span>
          </button>
        </li>
      </transition>
      <li v-if="computedError" class="my-8px mx-12px">
        <hr class="w-full h-2px bg-gradient-to-r from-red to-orange shadow-none border-none rounded-none" />
      </li>
      <!-- CURRENT SERVER - signed in and a-okay -->
      <div v-if="registered" class="relative z-0">
        <transition-group name="fade" mode="out-in">
          <UnraidLoading
            v-if="myServersLoading"
            class="w-36px my-8px mx-auto"
            :height="21"
            :title="$t('upc.myServers.loading')"
            key="gql-loading" />
          <div v-if="filteredServers" key="filteredServers">
            <transition name="fade" mode="out-in">
              <li
                v-if="!computedError"
                key="current-server"
                class="w-full py-8px px-16px flex flex-col"
                :class="{
                  'text-beta': currentServerStatus === 'online',
                  'text-orange cursor-not-allowed': currentServerStatus !== 'online',
                }"
              >
                <span class="flex flex-row items-center">
                  <UnraidSvgUiWarningFill v-if="currentServerStatus !== 'online'" class="fill-current w-12px h-12px mr-8px" />
                  <span v-else class="block w-12px h-12px mr-8px bg-green rounded-full"></span>
                  <span>{{ currentServerStatus !== 'online' ? $t('upc.myServers.mothership.notConnected') : $t('upc.myServers.mothership.connected') }}</span>
                </span>
              </li>
            </transition>
          </div>
        </transition-group>
      </div>
    </ul>
  </div>
</template>

<style lang="postcss">
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: theme('colors.orange') var(--color-alpha);
}
.custom-scrollbar::-webkit-scrollbar {
  width: 10px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: var(--color-alpha);
  border: 0;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: theme('colors.orange') ;
  border-radius: 4px;
  border: 0;
}
</style>
